import React from 'react';
import { ConditionGroup, AnswerOptionCondition, AnswerOptionConditionType, isConditionGroup } from 'shared/types/due-diligence';
import { ConditionNodeRenderer } from './ConditionNodeRenderer';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Trash, GitBranch } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { CONDITION_TYPES } from './condition-utils';

interface ConditionGroupRendererProps {
  group: ConditionGroup;
  depth: number;
  linkedLabels: Array<{ id: number; name: string }>;
  hasMultipleLabels: boolean;
  extractionQuestions: Array<{
    fieldName: string;
    displayName: string;
    extractionLabel?: string | null;
    extractionValueType?: 'number' | 'string' | 'date' | 'boolean' | null;
  }>;
  availableQuestions: Array<{
    id: number;
    displayName: string;
    fieldName: string;
    answerOptions?: Array<{
      id?: number;
      label: string;
      value: string;
      statusColor: string;
      active: boolean;
    }>;
  }>;
  currentQuestionId?: number;
  availableConditionTypes: typeof CONDITION_TYPES;
  onAddCondition: (groupId: string, type: AnswerOptionConditionType) => void;
  onAddGroup: (parentGroupId: string) => void;
  onRemoveNode: (nodeId: string) => void;
  onUpdateCondition: (conditionId: string, updates: Partial<AnswerOptionCondition>) => void;
  onChangeGroupLogic: (groupId: string) => void;
  onRemoveGroup?: (groupId: string) => void;
}

const DEPTH_COLORS = [
  'border-blue-400',
  'border-purple-400',
  'border-green-400',
  'border-orange-400',
];

export const ConditionGroupRenderer: React.FC<ConditionGroupRendererProps> = ({
  group,
  depth,
  linkedLabels,
  hasMultipleLabels,
  extractionQuestions,
  availableQuestions,
  currentQuestionId,
  availableConditionTypes,
  onAddCondition,
  onAddGroup,
  onRemoveNode,
  onUpdateCondition,
  onChangeGroupLogic,
  onRemoveGroup,
}) => {
  const borderColor = DEPTH_COLORS[depth % DEPTH_COLORS.length];
  const isRootGroup = depth === 0;

  const handleLogicToggle = () => {
    onChangeGroupLogic(group.id);
  };

  return (
    <div
      className={cn(
        'space-y-3 p-4 rounded-lg border-l-4',
        borderColor,
        depth > 0 ? 'bg-gray-50/50' : 'bg-white'
      )}
    >
      {/* Group header with delete button for non-root groups */}
      {!isRootGroup && onRemoveGroup && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <GitBranch size={16} weight="regular" />
            <span>Subgroep</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemoveGroup(group.id)}
            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
          >
            <Trash size={16} weight="regular" />
          </Button>
        </div>
      )}

      {/* Children with logic pills between them */}
      {group.children.map((child, index) => (
        <React.Fragment key={child.id}>
          {index > 0 && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleLogicToggle}
                className={cn(
                  'px-4 py-1.5 rounded-full text-xs font-medium',
                  'transition-all hover:scale-105',
                  group.logic === 'AND'
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                )}
              >
                {group.logic === 'AND' ? 'EN' : 'OF'}
              </button>
            </div>
          )}

          {isConditionGroup(child) ? (
            <ConditionGroupRenderer
              group={child}
              depth={depth + 1}
              linkedLabels={linkedLabels}
              hasMultipleLabels={hasMultipleLabels}
              extractionQuestions={extractionQuestions}
              availableQuestions={availableQuestions}
              currentQuestionId={currentQuestionId}
              availableConditionTypes={availableConditionTypes}
              onAddCondition={onAddCondition}
              onAddGroup={onAddGroup}
              onRemoveNode={onRemoveNode}
              onUpdateCondition={onUpdateCondition}
              onChangeGroupLogic={onChangeGroupLogic}
              onRemoveGroup={onRemoveNode}
            />
          ) : (
            <ConditionNodeRenderer
              condition={child}
              linkedLabels={linkedLabels}
              hasMultipleLabels={hasMultipleLabels}
              extractionQuestions={extractionQuestions}
              availableQuestions={availableQuestions}
              currentQuestionId={currentQuestionId}
              onUpdate={(updates) => onUpdateCondition(child.id, updates)}
              onRemove={() => onRemoveNode(child.id)}
            />
          )}
        </React.Fragment>
      ))}

      {/* Action buttons */}
      <div className="flex gap-2 pt-2">
        {/* Add condition button with popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-gray-600 hover:text-gray-900"
            >
              <Plus size={16} weight="bold" className="mr-1" />
              Voorwaarde toevoegen
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start">
            <div className="space-y-1">
              {availableConditionTypes.map((conditionType) => (
                <button
                  key={conditionType.type}
                  type="button"
                  onClick={() => onAddCondition(group.id, conditionType.type)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex-shrink-0 text-gray-500">
                    {/* Icon placeholder - would use actual icon component */}
                    <div className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">
                      {conditionType.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {conditionType.description}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Add subgroup button */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onAddGroup(group.id)}
          className="text-gray-600 hover:text-gray-900"
        >
          <GitBranch size={16} weight="bold" className="mr-1" />
          Subgroep
        </Button>
      </div>
    </div>
  );
};
